import API_ENDPOINTS from '../config/apiConfig';
import { api } from './api'; // Import the existing api instance from api.js

// Cache for budget data
export const budgetCache = {
  cacheExpiry: 2 * 60 * 1000, // 2 minutes - shorter than chart of accounts since budget data changes more frequently
  invalidateCache: () => {
    // Clear all cached entries by getting all keys that aren't functions or the expiry setting
    Object.keys(budgetCache).forEach(key => {
      if (key !== 'invalidateCache' && key !== 'cacheExpiry') {
        delete budgetCache[key];
      }
    });

  }
};

// Financial Budget API calls
export const getFinancialBudgets = async (filters = {}) => {
  try {
    // Allow passing exact filter parameters
    const { orgId, orgCode, locationId, year } = filters;
    
    // Create URL with query parameters
    let url = API_ENDPOINTS.FINANCIAL_BUDGET;
    const queryParams = [];
    
    // Add filters to query params if provided
    if (orgId) {
      queryParams.push(`orgId=${orgId}`);
    } else if (orgCode) {
      queryParams.push(`orgCode=${orgCode}`);
    }
    
    if (locationId) {
      queryParams.push(`locationId=${locationId}`);
    }
    
    if (year) {
      // If year is provided, filter for date range
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;
      queryParams.push(`startDate=${startDate}&endDate=${endDate}`);
    }
    
    // Add query parameters to URL if any exist
    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }
    
    // Generate a cache key based on the filters
    const cacheKey = JSON.stringify({ orgId, orgCode, locationId, year });
    
    // Check cache first using the specific filter key
    const now = Date.now();
    if (budgetCache[cacheKey] && 
        budgetCache[cacheKey].timestamp && 
        (now - budgetCache[cacheKey].timestamp) < budgetCache.cacheExpiry) {

      return budgetCache[cacheKey].data;
    }
    

    const response = await api.get(url);

    // Update cache with specific filter key
    budgetCache[cacheKey] = {
      data: response.data,
      timestamp: now
    };
    
    return response.data || [];
  } catch (error) {
    console.error('Error in getFinancialBudgets:', error);
    // Return empty array on error
    return [];
  }
};

export const getFinancialBudgetById = async (id) => {
  try {
    // Check cache first
    const now = Date.now();
    if (budgetCache.allBudgets && 
        budgetCache.timestamp && 
        (now - budgetCache.timestamp) < budgetCache.cacheExpiry) {
      return budgetCache.allBudgets;
    }
    
    const response = await api.get(`${API_ENDPOINTS.FINANCIAL_BUDGET}?id=${id}`);
    
    // Update cache
    budgetCache.allBudgets = response.data;
    budgetCache.timestamp = now;
    
    return response.data;
  } catch (error) {
    console.error('Error in getFinancialBudgetById:', error);
    return budgetCache.allBudgets || null;
  }
};

export const createFinancialBudget = async (budgetData) => {
  try {
    // Check that required fields are present
    if (!budgetData.orgCode || !budgetData.locationId || !budgetData.budgetDate || 
        (budgetData.budgetAmount === undefined && budgetData.amount === undefined)) {
      console.error('Missing required field(s) in budget data for create:', budgetData);
      throw new Error('Missing required fields (orgCode, locationId, budgetDate, budgetAmount) for budget entry');
    }
    
    // Ensure either accountNumber or chartOfAccountId is provided
    if (!budgetData.accountNumber && !budgetData.chartOfAccountId) {
      console.error('Budget entry missing both accountNumber and chartOfAccountId:', budgetData);
      throw new Error('Either accountNumber or chartOfAccountId is required for budget entry');
    }
    
    // Ensure we're using the correct field name (budgetAmount)
    const dataToSend = {
      ...budgetData,
      // If amount is provided but budgetAmount is not, copy amount to budgetAmount
      budgetAmount: budgetData.budgetAmount !== undefined ? budgetData.budgetAmount : budgetData.amount
    };
    

    const response = await api.post(API_ENDPOINTS.FINANCIAL_BUDGET, dataToSend);

    // Invalidate cache after creating new budget
    budgetCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in createFinancialBudget:', error);
    console.error('API error response:', error.response?.data);
    console.error('Failed with budget data:', JSON.stringify(budgetData, null, 2));
    throw error;
  }
};

export const createBulkFinancialBudgets = async (budgetDataArray) => {
  try {
    // Validate that we have an array
    if (!Array.isArray(budgetDataArray)) {
      throw new Error('Input must be an array of budget data');
    }
    if (budgetDataArray.length === 0) {

      return []; // Nothing to do
    }
    
    // Validate each entry in the array and normalize field names
    const processedBudgetData = budgetDataArray.map((entry, index) => {
      // Validate required fields
      if (!entry.orgCode || !entry.locationId || !entry.budgetDate || 
          (entry.budgetAmount === undefined && entry.amount === undefined)) {
        console.error('Invalid entry found in bulk budget data at index:', index, entry);
        throw new Error(`Missing required fields in budget entry at index ${index}`);
      }
      
      // Ensure either accountNumber or chartOfAccountId is provided
      if (!entry.accountNumber && !entry.chartOfAccountId) {
        console.error('Entry missing both accountNumber and chartOfAccountId at index:', index, entry);
        throw new Error(`Either accountNumber or chartOfAccountId is required at index ${index}`);
      }
      
      // Ensure correct field names
      return {
        ...entry,
        budgetAmount: entry.budgetAmount !== undefined ? entry.budgetAmount : entry.amount
      };
    });
    

    const response = await api.post(API_ENDPOINTS.FINANCIAL_BUDGET, processedBudgetData); 
    
    // Invalidate cache after creating new budgets
    budgetCache.invalidateCache();
    return response.data || []; // Ensure array is returned
  } catch (error) {
    console.error('Error in createBulkFinancialBudgets:', error);
    console.error('API error response:', error.response?.data);
    console.error('Failed with first few budget entries:', 
                 JSON.stringify(budgetDataArray.slice(0, 3), null, 2));
    throw error; // Re-throw the error
  }
};

export const updateFinancialBudget = async (budgetData) => {
  try {
    // Check that required fields are present
    if (!budgetData.id) {
      console.error('Missing required field id in budget data for update:', budgetData);
      throw new Error('Budget entry ID is required for updates');
    }
    
    // Ensure other core fields are present, though the API might only require the ID + changed fields
    if (!budgetData.orgCode || !budgetData.locationId || !budgetData.accountNumber || !budgetData.budgetDate || 
        (budgetData.budgetAmount === undefined && budgetData.amount === undefined)) {
      console.warn('Potentially missing fields in budget data for update:', budgetData);
      // Depending on API, this might not be a fatal error, but log it.
    }
    
    // Normalize field names
    const dataToSend = {
      ...budgetData,
      budgetAmount: budgetData.budgetAmount !== undefined ? budgetData.budgetAmount : budgetData.amount
    };
    

    const response = await api.put(API_ENDPOINTS.FINANCIAL_BUDGET, dataToSend);
    // Invalidate cache after updating budget
    budgetCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in updateFinancialBudget:', error);
    console.error('API error response:', error.response?.data);
    console.error('Failed with budget data:', JSON.stringify(budgetData, null, 2));
    throw error;
  }
};

export const deleteFinancialBudget = async (id) => {
  try {
    if (!id) {
      throw new Error('Budget entry ID is required for deletion');
    }
    // Ensure endpoint is correct, e.g., DELETE /financial-budget/{id}
    await api.delete(`${API_ENDPOINTS.FINANCIAL_BUDGET}?id=${id}`);
    // Invalidate cache after deleting budget
    budgetCache.invalidateCache();
    return true;
  } catch (error) {
    console.error('Error in deleteFinancialBudget:', error);
    throw error;
  }
}; 